import acceptLanguage from 'accept-language';
import { page } from '$app/stores';
import { derived, type Readable } from 'svelte/store';
import data from '$lib/region/data.json';

export const defaultLang = 'en';

// shopify languages
export const languages = {
  en: 'English',
  fr: 'Français'
} as const;

export const getAllLanguages = (): Record<string, string> => {
  const allLanguages = new Set<string>();

  Object.values(data).forEach((region) => {
    region.languages.forEach((lang) => allLanguages.add(lang));

    if (region.mainLanguage) {
      allLanguages.add(region.mainLanguage);
    }
  });

  const combinedLanguages: Record<string, string> = { ...languages };

  // Map all unique languages to their names
  allLanguages.forEach((lang) => {
    if (!combinedLanguages[lang]) {
      // If a specific naming format is needed (e.g., 'English-UK'), handle it here
      combinedLanguages[lang] = lang.includes('-')
        ? `${languages[lang.split('-')[0] as keyof typeof languages]}-${lang.split('-')[1].toUpperCase()}`
        : lang; // Default fallback to use the raw code
    }
  });

  return combinedLanguages;
};

export type Language = keyof typeof languages;

export const isLang = (lang: string): lang is Language => {
  return Object.keys(languages).includes(lang) && lang !== defaultLang;
};

export const lang: Readable<Language> = derived(page, ($page) => {
  return isLang($page.params.lang) ? $page.params.lang : defaultLang;
});

export const getRegionLanguage = (region: string, languageValue: string) => {
  const regionData = data[region as keyof typeof data];

  if (!regionData) {
    return defaultLang;
  }

  const { languages, mainLanguage } = regionData;

  if (languages.includes(languageValue)) {
    // If the mainLanguage is a variation of the language, return mainLanguage
    if (mainLanguage && mainLanguage.startsWith(languageValue)) {
      return mainLanguage;
    }

    return languageValue;
  }

  return defaultLang;
};

export const regionLanguage: Readable<string> = derived(page, ($page) => {
  const region = $page.params?.region;
  const languageValue = isLang($page.params.lang) ? $page.params.lang : defaultLang;

  return getRegionLanguage(region, languageValue);
});

export const getOptimalLanguage = (acceptLanguageHeader: string | null): Language => {
  try {
    acceptLanguage.languages(Object.keys(languages));
    const optimalLanguage = acceptLanguage.get(acceptLanguageHeader);

    return optimalLanguage && isLang(optimalLanguage) ? optimalLanguage : defaultLang;
  } catch (error) {
    console.error(error);
    return defaultLang;
  }
};
